import { type ColumnDataType } from '@blaze-shared/api-contracts-domain';
import { splitValueWithUnit, type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';
import { isNullish } from '@blaze-shared/shared-util';

export const DURATION_DAY = 86400;
export const DURATION_HOUR = 3600;

const converters: Partial<Record<ColumnDataType, (input: any) => number>> = {
  Date: dateTimeConverter,
  DateTime: dateTimeConverter,
  Duration: durationConverter,
};

export function getCountdownDataFieldValue(
  value: any,
  valueDataType: ColumnDataType | undefined,
): number {
  if (typeof value === 'number') {
    return value;
  }
  if (!valueDataType) {
    return 0;
  }
  const converter = converters[valueDataType];
  if (!converter) {
    return 0;
  }
  return converter(value);
}

export function getCountdownDurationValue(value: string | undefined): number {
  if (isNullish(value)) {
    return 0;
  }
  const parsed = splitValueWithUnit(value as ValueWithUnitOfMeasure);
  switch (parsed?.unit) {
    case 'days':
      return parsed.value * DURATION_DAY;
    case 'hrs':
      return parsed.value * DURATION_HOUR;
    case 'min':
      return parsed.value * 60;
    case 's':
      return parsed.value;
    default:
      return 0;
  }
}

export function toStringWithLeading0(value: number, minLen: number): string {
  return value.toString().padStart(minLen, '0');
}

export function getRemainingSeconds(deadlineMs: number, now: number = Date.now()): number {
  return Math.max(0, Math.round((deadlineMs - now) / 1000));
}

export function dateTimeConverter(input: string, refDate?: Date): number {
  refDate ??= new Date();
  return (new Date(input).getTime() - refDate.getTime()) / 1000;
}

export function durationConverter(input: string): number {
  let days = 0;
  let timePart = input;

  const dotIndex = input.indexOf('.');
  const colonIndex = input.indexOf(':');
  if (dotIndex !== -1 && dotIndex < colonIndex) {
    days = parseInt(input.slice(0, dotIndex), 10);
    timePart = input.slice(dotIndex + 1);
  }

  const parts = timePart.split(':');
  const hrs = parseInt(parts[0], 10);
  const mins = parseInt(parts[1], 10);
  const secs = parts[2] ? parseFloat(parts[2]) : 0;

  return days * DURATION_DAY + hrs * DURATION_HOUR + mins * 60 + secs;
}
